import axios from '@/_utils/axios';
import { getLineageMetaTitle } from '@/_utils/utils';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import useDebounce from '@/components/_hooks/useDebounce';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { addVersionKeys } from '@/redux/slices/orderPlacementSlice';
import { TitleEntity, TitleResponse } from '@/redux/slices/slice_types';
import { TitleResponseV2 } from '@/redux/slices/titleV2Slice.types';
import styled from '@emotion/styled';
import { ClickAwayListener, Fade, Grid, IconButton, TextField, Typography } from '@mui/material';
import { WidgetProps } from '@rjsf/utils';
import React, { useContext, useEffect, useRef, useState } from 'react';
import OutputVersionSelect from '../../Form/OutputVersionSelect';
import PolarisIcon from '../../PolarisIcon';
import Spinner from '../../Spinner';

const OUTPUT_VERSION_CONTAINER_SIZE = 7;

const ProgramVersionOutputWidget = (props: WidgetProps) => {
  const { id, value, required, label, onChange, formContext, disabled, readonly, formRef, name, rawErrors = [] } = props;
  const programIdKey = `program_id_${id}`;
  const dispatch = useAppDispatch();
  const isTitleApiV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const [shouldSearch, setShouldSearch] = useState(false);
  const versionKeys = useAppSelector(selectVersionKeys);
  const [searchInput, setSearchInput] = useState(value && versionKeys[value]?.program_id ? versionKeys[value]?.program_id : '');
  const debouncedSearchValue = useDebounce(searchInput, 500);
  const [results, setResults] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [loadingVersions, setLoadingVersions] = useState<boolean>(false);
  const programIdField = useRef<HTMLDivElement | null>(null);
  const isDropdownShown = !selected && results.length > 0;
  const displayLabel = 'Title';
  const assetTypeValueMap = useConfigValue(['ingestManagementConfig', 'assetTypeValueMap']);
  const setProgram = (programId: string) => {
    const change = {
      [name]: null,
      [programIdKey]: programId
    };

    formContext.onFormChange(change);
  };

  useEffect(() => {
    if (value && versionKeys[value]) {
      setSelected(versionKeys[value]);
    } else if (formRef?.current?.state?.formData?.[programIdKey]) {
      onProgramChanged(formRef?.current?.state?.formData?.[programIdKey]);
    } else {
      onProgramChanged(formContext.programId);
    }
  }, []);

  useEffect(() => {
    if (formContext.defaultTitle) {
      setSelected(formContext.defaultTitle);
    }
  }, [formContext.defaultTitle]);

  const onProgramChanged = (programId?: number) => {
    if (programId) {
      const program = versionKeys[programId];
      setSelected(program);
      setProgram(program.program_id);
      setSearchInput(program.program_id);
    }
  };

  const fetchTitle = (search: string) => {
    const abortController = new AbortController();
    setSearching(true);
    axios
      .post(
        '/inventory/titles/search/',
        {
          search,
          entity_type_filter: ['episode', 'feature']
        },
        { signal: abortController.signal }
      )
      .then((response) => {
        setResults(response.data?.data);
        setSearching(false);
      })
      .catch((err) => {
        if (err.name === 'CanceledError') return;
        setSearching(false);
      });

    return abortController;
  };

  useEffect(() => {
    if (!shouldSearch) {
      setShouldSearch(true);
      return;
    } else {
      if (!debouncedSearchValue?.length) {
        return;
      }
      setSearching(true);
      const abortController = fetchTitle(debouncedSearchValue);
      return () => abortController.abort();
    }
  }, [debouncedSearchValue]);

  const handleInputChange = (e: React.ChangeEvent) => {
    setSelected(null);
    if (!(e.target as HTMLInputElement).value.length) {
      setResults([]);
    }
    setSearchInput((e.target as HTMLInputElement).value);
  };

  const handleSelection = (result: TitleEntity) => {
    setResults([]);
    setSearchInput('');
    setLoadingVersions(true);
    dispatch(addVersionKeys([result]));
    axios
      .get<TitleResponse | TitleResponseV2>(`/inventory/titles/${result.id}/`, isTitleApiV2 ? { baseURL: '/api/v2/' } : undefined)
      .then((response) => {
        setSelected(response.data.data);
        fetchTitle(result.id.toString());
        setProgram(result.program_id);
        setLoadingVersions(false);
      })
      .catch((error) => {
        console.error(error);
        setLoadingVersions(false);
      });
  };

  const restoreSelectedProgram = () => {
    setSelected(versionKeys[formContext?.formData?.[programIdKey]]);
  };

  const onKeyDown =
    (source: 'text' | 'option') => (evt: React.KeyboardEvent<HTMLDivElement | HTMLInputElement | HTMLTextAreaElement>, selection?: any) => {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        if (source === 'option') {
          handleSelection(selection);
        }
      }
      if (evt.key === 'Escape') {
        if (isDropdownShown) {
          setResults([]);
        }
        if (source === 'option') {
          (document.querySelector('#program_id') as HTMLInputElement | undefined)?.focus();
        } else {
          restoreSelectedProgram();
        }
      }
      if (source === 'text' && evt.key === 'ArrowDown') {
        evt.preventDefault();
        (document.querySelector('#select-dropdown ul li input') as HTMLInputElement | undefined)?.focus();
      }
    };

  const onVersionSelected = (val: string) => {
    const usage = versionKeys[val]?.meta?.usage;

    if (!!assetTypeValueMap && !!formRef?.current && !!formRef.current?.props?.schema?.properties?.asset_type) {
      const change = {
        ...formRef.current.state.formData,
        [name]: val,
        asset_type: assetTypeValueMap[usage]
      };

      if (!Object.keys(assetTypeValueMap).includes(usage)) {
        change.asset_type = null;
      }

      formRef.current.onChange(change);
    } else {
      onChange(val);
    }
  };

  return (
    <ProgramVersionOutputWrapper container spacing={2}>
      {!readonly && (
        <Grid item xs={5}>
          {!selected?.id ? (
            <TextField
              role="combobox"
              aria-labelledby="select button"
              aria-haspopup="listbox"
              aria-expanded={!selected && results.length > 0 ? 'true' : undefined}
              aria-controls="select-dropdown"
              id="program_id"
              name="program_id"
              ref={programIdField}
              value={searchInput}
              variant="filled"
              onChange={handleInputChange}
              placeholder={`Search for a ${displayLabel}`}
              label={displayLabel}
              fullWidth
              disabled={loadingVersions || disabled}
              InputProps={{
                onBlur: restoreSelectedProgram,
                onKeyDown: onKeyDown('text'),
                autoComplete: 'off',
                endAdornment: (searching || loadingVersions) && <SearchingSpinner size={20} />
              }}
            />
          ) : (
            <SelectedProgram>
              <Grid container justifyContent="space-between" alignItems="center">
                <Undo>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setSelected(null);
                      setSearchInput('');
                    }}
                  >
                    <PolarisIcon icon="undo" />
                  </IconButton>
                </Undo>
                <div>
                  <strong>{selected?.lineage_meta?.series_name}</strong>{' '}
                  <Typography variant="caption">[{selected.program_id || selected.external_id}]</Typography>
                  <div>
                    <Typography variant="caption">{getLineageMetaTitle(selected)}</Typography>
                  </div>
                </div>
              </Grid>
            </SelectedProgram>
          )}
          <Fade in={isDropdownShown}>
            <ResultsContainer id="select-dropdown" role="listbox">
              <ClickAwayListener onClickAway={() => setResults([])}>
                <Results>
                  {results.map((result: any) => {
                    return (
                      <li role="option" key={result.external_id} onMouseDown={() => handleSelection(result)}>
                        <InvisibleRadio onKeyDown={(e) => onKeyDown('option')(e, result)} type="radio" id={result.external_id} name="hidden-radio" />
                        <DropdownContent className="dropdown-content" container justifyContent="space-between" alignItems="center">
                          <div>
                            <strong>{result.lineage_meta?.series_name || result.name}</strong>
                            <div>
                              <Typography variant="caption">{getLineageMetaTitle(result)}</Typography>
                            </div>
                          </div>
                          <Typography variant="caption">{result.program_id ? result.program_id : result.external_id}</Typography>
                        </DropdownContent>
                      </li>
                    );
                  })}
                </Results>
              </ClickAwayListener>
            </ResultsContainer>
          </Fade>
        </Grid>
      )}
      {readonly ? (
        <Grid item xs={12}>
          <TextField value={value || '-'} label={label} />
        </Grid>
      ) : (
        <Grid item xs={OUTPUT_VERSION_CONTAINER_SIZE}>
          <OutputVersionSelect
            value={value}
            label={label}
            disabled={(!selected?.id && !value) || disabled}
            titleId={versionKeys[value]?.program_id || selected?.program_id ? selected?.program_id || versionKeys[value]?.program_id : undefined}
            onChange={(e) => onVersionSelected(String(e.target.value))}
            required={required}
            error={rawErrors?.length > 0}
            helperText={rawErrors[0] ?? ''}
            squashed
          />
        </Grid>
      )}
    </ProgramVersionOutputWrapper>
  );
};

export default ProgramVersionOutputWidget;

const ResultsContainer = styled.div`
  width: 100%;
  z-index: 100;
`;

const ProgramVersionOutputWrapper = styled(Grid)`
  .MuiAutocomplete-root {
    width: 100%;
  }

  & + .MuiList-root {
    max-width: ${(100 / 12) * OUTPUT_VERSION_CONTAINER_SIZE}%;
    margin-left: auto;
    width: 100%;
  }

  & + ul[id*='output_version'] {
    display: none;
  }
`;

const InvisibleRadio = styled.input`
  position: absolute;
  opacity: 0;
  left: 0;
`;

const Undo = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 5px;
`;

const SelectedProgram = styled.div`
  position: relative;
  background: ${({ theme }) => (theme.palette.mode === 'dark' ? 'black' : theme.palette.grey[300])};
  padding: ${({ theme }) => theme.spacing(1, 2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  overflow: hidden;

  .MuiIconButton-root {
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1;
  }

  &:before {
    display: block;
    pointer-events: none;
    content: '';
    background: ${({ theme }) => `linear-gradient(to right, transparent 0%, ${theme.palette.background.default}) 80%`};
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    width: 100%;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    &:before {
      opacity: 0.5;
      transition: opacity 0.2s;
    }
    .MuiIconButton-root {
      opacity: 1;
      transition: opacity 0.2s;
    }
  }
`;

const DropdownContent = styled(Grid)`
  padding: ${({ theme }) => theme.spacing(1, 2)};
`;

const Results = styled.ul`
  position: absolute;
  background: ${({ theme }) => theme.palette.background.default};
  z-index: 100;
  list-style: none;
  padding: 0;
  padding-top: ${({ theme }) => theme.spacing(1)};
  margin: 0;
  max-height: 200px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  box-shadow: ${({ theme }) => theme.shadows[10]};
  width: 80%;
  max-width: 340px;
  min-width: 100px;
  overflow: auto;
  font-size: 0.875rem;
  .MuiTypography-caption {
    font-size: 0.75rem;
  }
  li {
    cursor: pointer;
    align-items: center;
    transition: background 0.2s ease-in-out;

    & > input:focus ~ .dropdown-content {
      background: ${({ theme }) => theme.palette.action.hover};
    }

    &:hover {
      background: ${({ theme }) => theme.palette.action.hover};
      transition: background 0.2s ease-in-out;
    }
  }
`;

const SearchingSpinner = styled(Spinner)`
  left: auto !important;
  right: 10px;
`;
